import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { v4 as uuidv4 } from 'uuid';

import { RootState } from '../../../app/rootReducer';
import { AppDispatch } from '../../../app/store';
import { FeatureFlags } from '../../../common/constants/featureFlags';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { getDecisionTypeForLifecycle } from '../../../common/helpers/decisionType';
import { ProductConfig } from '../../../common/productConfig/productConfig';
import {
  ISnacksProps,
  SnackBarType,
} from '../../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../../layout/SnackbarContext';
import { IDecisionRequest } from '../interfaces/IDecisionRequest';
import { IDecisionUpdateRequest } from '../interfaces/IDecisionUpdateRequest';
import { DECISION_CONFIGS } from './decisionConfigs';
import {
  AutoSendOptions,
  patchDecision,
  patchDecisionLeadingToCLR,
  postDecision,
} from './decisionSlice';

export function useDecisionSubmit(applicationId: string, configKey: string) {
  const dispatch = useDispatch<AppDispatch>();
  const config = DECISION_CONFIGS[configKey];

  const { flags } = useContext(FeatureFlagsContext);
  const useV2 = flags[FeatureFlags.UW_NORA_DECISION_V2_ENABLED] || false;

  const lifecycle = useSelector(
    (state: RootState) => state.application.applicationLifecycleV2,
  );

  const { createDecisionFailed, createDecisionError, createDecisionErrorType } =
    useSelector((state: RootState) => state.decision);

  const { openSnacks, setSnacks } = useContext(SnackbarContext);

  useEffect(() => {
    if (createDecisionFailed) {
      const snack: ISnacksProps = {
        id: uuidv4(),
        type: (createDecisionErrorType as SnackBarType) || SnackBarType.ALERT,
        message: createDecisionError,
      };
      setSnacks([...openSnacks, snack]);
    }
  }, [createDecisionFailed, createDecisionError, createDecisionErrorType]);

  const decisionType = getDecisionTypeForLifecycle(lifecycle);

  const submitCreate = useCallback(
    (
      formData: any,
      productConfig?: ProductConfig | null,
      isPolicyActivated?: boolean,
      isCarrierDecision?: boolean,
      autoSendOptions?: AutoSendOptions,
    ) => {
      const payload: IDecisionRequest = {
        decision: config.decision,
        decisionType,
        ...config.buildPayload(formData, productConfig, isPolicyActivated),
      } as IDecisionRequest;
      dispatch(postDecision(applicationId, payload, isCarrierDecision, useV2, autoSendOptions));
    },
    [applicationId, config, decisionType, dispatch, useV2],
  );

  const submitEdit = useCallback(
    (
      decisionId: string,
      update: IDecisionUpdateRequest,
      isLeadingToCLR: boolean,
    ) => {
      dispatch(
        isLeadingToCLR
          ? patchDecisionLeadingToCLR(applicationId, decisionId, update)
          : patchDecision(applicationId, decisionId, update, useV2),
      );
    },
    [applicationId, dispatch, useV2],
  );

  return { decisionType, submitCreate, submitEdit };
}
